import { onMounted, ref, watch } from 'vue';
import { useI18n } from '@/composables';
import { getDictionaryApiRequest } from '@/services/api/dictionary-api';

type FlowMultiValueConditionEntry = {
  predicate: string;
  values: string[];
};

type FlowConditions = Record<string, FlowMultiValueConditionEntry>;

type FlowDictionaryCheckboxConditionOptions = {
  conditionKey: string;
  dictionaryKey: string;
  initialLoading?: boolean;
};

export const useFlowDictionaryCheckboxCondition = (
  conditions: FlowConditions,
  options: FlowDictionaryCheckboxConditionOptions,
) => {
  const { t } = useI18n();

  const loading = ref(options.initialLoading ?? true);
  const data = ref<string[]>([]);
  const selected = ref<Array<string | false>>([]);
  const predicate = ref('EQUALS');

  watch(
    () => conditions,
    () => {
      if (!conditions[options.conditionKey]) {
        conditions[options.conditionKey] = {
          values: [],
          predicate: 'EQUALS',
        };
      }
    },
    { immediate: true },
  );

  watch(
    selected,
    (value) => {
      conditions[options.conditionKey].values = value.filter((item) => item) as string[];
    },
    { deep: true },
  );

  watch(predicate, (value) => {
    conditions[options.conditionKey].predicate = value;
  });

  onMounted(() => {
    if (!options.initialLoading) {
      loading.value = true;
    }

    getDictionaryApiRequest(options.dictionaryKey, {}).then((types) => {
      const dictionaryItems = types as Array<{ name: string }>;
      data.value = dictionaryItems.map((item) => item.name);

      const condition = conditions[options.conditionKey];
      if (condition?.values) {
        predicate.value = condition.predicate || predicate.value;
        selected.value = data.value.map((name) =>
          condition.values.indexOf(name) !== -1 ? name : false,
        );
      } else {
        selected.value = new Array(dictionaryItems.length).fill(false);
      }

      loading.value = false;
    });
  });

  return {
    t,
    loading,
    data,
    selected,
    predicate,
  };
};
